/**
 * SDR Stock Manager - 3-Slot FIFO Rolling Backup Manager & JSON Export/Import
 * @module state/backups
 */

import { STORAGE_KEYS } from '../config/constants.js';
import { getState, setState } from './store.js';
import { saveLocalData, isQuotaExceededError } from './storage.js';

let sdrBackupsCache = null;

/**
 * Strips Base64 data URL images from inventory lots to prevent quota overflows during backup.
 * Keeps standard HTTPS image URLs intact.
 * @param {import('../types/index.js').InventoryItem[]} inventory
 * @returns {import('../types/index.js').InventoryItem[]}
 */
export function sanitizeInventoryForSnapshot(inventory) {
  if (!Array.isArray(inventory)) return [];
  return inventory.map(item => {
    const copy = { ...item };
    if (copy.image && copy.image.startsWith('data:image')) {
      copy.image = '';
    }
    return copy;
  });
}

/**
 * Retrieves all stored backup snapshots (maximum 3 slots).
 * @returns {Array<any>}
 */
export function getStoredBackups() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.BACKUPS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        sdrBackupsCache = parsed;
        return parsed;
      }
    }
  } catch (err) {
    console.warn('Failed to parse stored backups:', err);
  }
  return Array.isArray(sdrBackupsCache) ? sdrBackupsCache : [];
}

/**
 * Persists backup snapshots with quota protection and fallback sanitization.
 * @param {Array<any>} backups
 * @returns {boolean}
 */
export function saveStoredBackups(backups) {
  const target = Array.isArray(backups) ? backups.slice(0, 3) : [];
  sdrBackupsCache = target;
  try {
    localStorage.setItem(STORAGE_KEYS.BACKUPS, JSON.stringify(target));
    return true;
  } catch (err) {
    console.warn('Primary backup storage failed (quota limit), optimizing...', err);
    try {
      const lightweight = target.map(b => {
        const copy = JSON.parse(JSON.stringify(b));
        if (copy.data && Array.isArray(copy.data.inventory)) {
          copy.data.inventory = sanitizeInventoryForSnapshot(copy.data.inventory);
        }
        return copy;
      });
      localStorage.setItem(STORAGE_KEYS.BACKUPS, JSON.stringify(lightweight));
      sdrBackupsCache = lightweight;
      return true;
    } catch (err2) {
      console.error('Failed to save backups after payload reduction:', err2);
      return false;
    }
  }
}

/**
 * Creates a new point-in-time snapshot and stores it in the 3-slot rolling FIFO queue.
 * @param {Partial<import('../types/index.js').AppState>} [stateOverride]
 * @returns {Object} Newly created snapshot
 */
export function createManualBackup(stateOverride = null) {
  const currentState = stateOverride || getState();
  const backups = getStoredBackups();
  const now = new Date();
  const formatted =
    now.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' }) +
    ' ' +
    now.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });

  const activeItems = (currentState.inventory || []).filter(i => !i.deleted);
  const activeTx = (currentState.transactions || []).filter(t => !t.deleted);
  const activeAudits = (currentState.stockAudits || []).filter(a => !a.deleted);
  const activeStockOutMaster = (currentState.stockOutMaster || []).filter(s => !s.deleted);

  const newSnapshot = {
    id: 'backup_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
    createdAt: now.toISOString(),
    timestamp: now.toISOString(),
    formattedDate: formatted,
    inventoryCount: activeItems.length,
    summary: {
      totalItems: activeItems.length,
      totalTransactions: activeTx.length,
      totalAudits: activeAudits.length,
      totalStockOut: activeStockOutMaster.length
    },
    data: {
      inventory: sanitizeInventoryForSnapshot(currentState.inventory || []),
      transactions: JSON.parse(JSON.stringify(currentState.transactions || [])),
      stockAudits: JSON.parse(JSON.stringify(currentState.stockAudits || [])),
      stockOutMaster: JSON.parse(JSON.stringify(currentState.stockOutMaster || [])),
      stockOutDetails: JSON.parse(JSON.stringify(currentState.stockOutDetails || [])),
      settings: JSON.parse(JSON.stringify(currentState.settings || {}))
    }
  };

  backups.unshift(newSnapshot);
  if (backups.length > 3) {
    backups.splice(3);
  }

  saveStoredBackups(backups);
  return newSnapshot;
}

/**
 * Restores a backup snapshot by ID into application state and localStorage.
 * @param {string} backupId
 * @param {boolean} [applyToStore=true]
 * @returns {Object|null} Restored data payload or null if not found
 */
export function restoreBackup(backupId, applyToStore = true) {
  const backups = getStoredBackups();
  const backup = backups.find(b => b.id === backupId);
  if (!backup || !backup.data) {
    return null;
  }

  const restored = {
    inventory: Array.isArray(backup.data.inventory) ? JSON.parse(JSON.stringify(backup.data.inventory)) : [],
    transactions: Array.isArray(backup.data.transactions) ? JSON.parse(JSON.stringify(backup.data.transactions)) : [],
    stockAudits: Array.isArray(backup.data.stockAudits) ? JSON.parse(JSON.stringify(backup.data.stockAudits)) : [],
    stockOutMaster: Array.isArray(backup.data.stockOutMaster) ? JSON.parse(JSON.stringify(backup.data.stockOutMaster)) : [],
    stockOutDetails: Array.isArray(backup.data.stockOutDetails) ? JSON.parse(JSON.stringify(backup.data.stockOutDetails)) : [],
    settings: backup.data.settings && typeof backup.data.settings === 'object'
      ? JSON.parse(JSON.stringify(backup.data.settings))
      : {}
  };

  if (applyToStore) {
    setState(restored, { silent: false });
    saveLocalData();
  }

  return restored;
}

/**
 * Deletes a backup snapshot by ID.
 * @param {string} backupId
 * @returns {boolean} True if deleted, false if not found
 */
export function deleteBackup(backupId) {
  const backups = getStoredBackups();
  const target = backups.find(b => b.id === backupId);
  if (!target) return false;

  const filtered = backups.filter(b => b.id !== backupId);
  saveStoredBackups(filtered);
  return true;
}

/**
 * Generates an exportable JSON object containing current state and rolling snapshots.
 * @param {Partial<import('../types/index.js').AppState>} [stateOverride]
 * @param {Array<any>} [customBackups]
 * @returns {Object}
 */
export function exportBackupJson(stateOverride = null, customBackups = null) {
  const currentState = stateOverride || getState();
  const backups = customBackups || getStoredBackups();

  return {
    version: '2.0',
    exportedAt: new Date().toISOString(),
    currentState: {
      inventory: currentState.inventory || [],
      transactions: currentState.transactions || [],
      stockAudits: currentState.stockAudits || [],
      stockOutMaster: currentState.stockOutMaster || [],
      stockOutDetails: currentState.stockOutDetails || [],
      settings: currentState.settings || {}
    },
    savedBackups: backups
  };
}

/**
 * Validates and parses imported backup JSON payload.
 * @param {string|Object} payload - JSON string or parsed object
 * @returns {{ inventory: any[], transactions: any[], stockAudits: any[], stockOutMaster: any[], stockOutDetails: any[], settings: Object }}
 * @throws {Error} If payload fails schema validation
 */
export function importBackupJson(payload) {
  const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('โครงสร้างไฟล์สำรองไม่ถูกต้อง (Invalid JSON structure)');
  }

  const restoreTarget = parsed.currentState || parsed.data || parsed;
  if (!restoreTarget || (!Array.isArray(restoreTarget.inventory) && !Array.isArray(parsed.inventory))) {
    throw new Error('โครงสร้างไฟล์สำรองไม่ถูกต้อง ไม่พบข้อมูลสินค้า (Inventory)');
  }

  return {
    inventory: restoreTarget.inventory || parsed.inventory || [],
    transactions: restoreTarget.transactions || parsed.transactions || [],
    stockAudits: restoreTarget.stockAudits || parsed.stockAudits || [],
    stockOutMaster: restoreTarget.stockOutMaster || parsed.stockOutMaster || [],
    stockOutDetails: restoreTarget.stockOutDetails || parsed.stockOutDetails || [],
    settings: restoreTarget.settings || parsed.settings || {}
  };
}
